/**
 * Conexa API Service
 * Integração com a API Conexa para cadastro de clientes e pessoas
 * Documentação: https://documenter.getpostman.com/view/25182821/2s93RZMpcB
 */

import { CnpjResponse, CnpjSocio } from './cnpjService';

export interface ConexaCustomerPayload {
  companyId: number;
  name: string;
  tradeName: string | null;
  pronunciation: string | null;
  fieldOfActivity: string | null;
  notes: string | null;
  cellNumber: string | null;
  website: string | null;
  hasLoginAccess: boolean;
  login: string | null;
  password: string | null;
  automaticallyIssueNfse: 'notIssue' | 'issue';
  notesNfse: string | null;
  taxDeductions: {
    iss: boolean;
  };
  legalPerson: {
    cnpj: string;
    foundationDate: string | null;
    stateInscription: string | null;
    municipalInscription: string | null;
  };
  address: {
    zipCode: string;
    state: string;
    city: string;
    street: string;
    number: string;
    neighborhood: string;
    additionalDetails: string | null;
  };
  phones: string[];
  emailsMessage: string[];
  emailsFinancialMessages: string[];
}

export interface ConexaPersonPayload {
  customerId: number;
  name: string;
  nationality: string | null;
  placeOfBirth: string | null;
  maritalStatus: 'single' | 'married' | 'divorced' | 'widowed' | 'other' | null;
  isCompanyPartner: boolean;
  isGuarantor: boolean;
  cpf: string | null;
  rg: string | null;
  birthDate: string | null;
  issuingAuthority: string | null;
  sex: 'M' | 'F' | null;
  cellNumber: string | null;
  jobTitle: string | null;
  profession: string | null;
  resume: string | null;
  notes: string | null;
  accessId: string | null;
  isForeign: boolean;
  emails: string[];
  address: {
    zipCode: string;
    state: string;
    city: string;
    street: string;
    number: string;
    neighborhood: string;
    additionalDetails: string | null;
  } | null;
  phones: string[];
}

export interface ConexaCustomerResponse {
  id: number;
  name: string;
  tradeName: string | null;
}

export interface ConexaPersonResponse {
  id: number;
  name: string;
  customerId: number;
}

export interface ConexaError {
  message: string;
  errors?: Record<string, string[]>;
}

/** Dados de um responsável (sócio administrador, financeiro ou operacional) */
export interface ResponsavelData {
  nome: string;
  cpf: string;
  email: string;
  celular: string;
  cargo: string;
  isSocio: boolean;
  isFinanceiro: boolean;
  isAdministrador: boolean;
  isOperacional?: boolean;
  cadastrar?: boolean; // Se true, será cadastrado no Conexa
  assinaContrato?: boolean; // Se true, assina o contrato
  testemunhaContrato?: boolean; // Se true, é testemunha do contrato
}

/**
 * Formata CNPJ para o padrão XX.XXX.XXX/XXXX-XX
 */
function formatCnpjForConexa(cnpj: string): string {
  const clean = cnpj.replace(/[^\d]/g, '');
  if (clean.length !== 14) return cnpj;
  return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12)}`;
}

/**
 * Formata telefone para o padrão (XX) XXXX-XXXX ou (XX) XXXXX-XXXX
 */
function formatPhoneForConexa(phone: string): string {
  const clean = phone.replace(/[^\d]/g, '');
  if (clean.length === 10) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  }
  if (clean.length === 11) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  }
  return phone;
}

/**
 * Formata CEP para o padrão XXXXX-XXX
 */
function formatCepForConexa(cep: string): string {
  const clean = cep.replace(/[^\d]/g, '');
  if (clean.length !== 8) return cep;
  return `${clean.slice(0, 5)}-${clean.slice(5)}`;
}

/**
 * Extrai o estado (UF) do endereço ou cidade
 * Por enquanto retorna SP como padrão - idealmente deveria vir do CNPJ lookup
 */
function extractState(city: string): string {
  // Mapeamento básico de cidades conhecidas para UF
  const cityStateMap: Record<string, string> = {
    'são paulo': 'SP',
    'rio de janeiro': 'RJ',
    'belo horizonte': 'MG',
    'curitiba': 'PR',
    'porto alegre': 'RS',
    'florianópolis': 'SC',
    'campinas': 'SP',
    'brasília': 'DF',
    'salvador': 'BA',
    'fortaleza': 'CE',
    'recife': 'PE',
    'manaus': 'AM',
    'belém': 'PA',
    'goiânia': 'GO',
  };
  
  const normalizedCity = city.toLowerCase().trim();
  return cityStateMap[normalizedCity] || 'SP'; // Default para SP
}

/**
 * Formata CPF para o padrão XXX.XXX.XXX-XX
 */
function formatCpfForConexa(cpf: string): string {
  const clean = cpf.replace(/[^\d]/g, '');
  if (clean.length !== 11) return cpf;
  return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9)}`;
}

/**
 * Identifica sócios administradores a partir da qualificação
 * Qualificações típicas de administradores: 49 (Sócio-Administrador), 05 (Administrador)
 */
export function identificarSociosAdministradores(socios: CnpjSocio[]): CnpjSocio[] {
  const qualificacoesAdmin = [5, 49, 10, 16, 22, 37, 38, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66];
  return socios.filter(socio => {
    const qualId = socio.qualificacao_socio?.id;
    return qualificacoesAdmin.includes(qualId);
  });
}

/**
 * Converte dados de sócio do CNPJ para ResponsavelData
 */
export function socioToResponsavel(socio: CnpjSocio): ResponsavelData {
  // Limpar CPF mascarado (ex: ***293787**) - só usar se for íntegro
  const cpfRaw = socio.cpf_cnpj_socio || '';
  const cpfLimpo = cpfRaw.includes('*') ? '' : cpfRaw;
  return {
    nome: socio.nome,
    cpf: cpfLimpo,
    email: '',
    celular: '',
    cargo: socio.qualificacao_socio?.descricao || 'Sócio',
    isSocio: true,
    isFinanceiro: false,
    isAdministrador: [5, 49].includes(socio.qualificacao_socio?.id || 0),
    cadastrar: false, // Por padrão não cadastra, usuário escolhe
  };
}

export const conexaService = {
  /**
   * Cria um novo cliente no Conexa
   */
  async createCustomer(payload: ConexaCustomerPayload): Promise<ConexaCustomerResponse> {
    const response = await fetch('/api/conexa?action=customer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorData = await response.json();
        console.error('[ERRO Conexa] Status:', response.status, 'Detalhes:', JSON.stringify(errorData, null, 2));
        throw new Error(errorData.error || errorData.message || `Erro ao cadastrar cliente no Conexa (${response.status})`);
      }
      throw new Error(`Erro ao cadastrar cliente no Conexa (${response.status})`);
    }

    return response.json();
  },

  /**
   * Cria uma nova pessoa (responsável) vinculada a um cliente no Conexa
   */
  async createPerson(payload: Record<string, unknown>): Promise<ConexaPersonResponse> {
    const response = await fetch('/api/conexa?action=person', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorData: ConexaError = await response.json();
        throw new Error(errorData.message || `Erro ao cadastrar pessoa no Conexa (${response.status})`);
      }
      throw new Error(`Erro ao cadastrar pessoa no Conexa (${response.status})`);
    }

    return response.json();
  },

  /**
   * Prepara o payload para criação de cliente no Conexa
   * com todos os dados disponíveis do CNPJ
   */
  prepareCustomerPayload(data: {
    name: string;
    tradeName?: string;
    cnpj: string;
    phone: string;
    phone2?: string;
    email: string;
    emailFinanceiro?: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    complement?: string;
    province: string;
    city: string;
    state?: string;
    notes?: string;
    fieldOfActivity?: string;
    foundationDate?: string;
    stateInscription?: string;
    website?: string;
  }): ConexaCustomerPayload {
    const phones: string[] = [];
    if (data.phone) phones.push(formatPhoneForConexa(data.phone));
    if (data.phone2) phones.push(formatPhoneForConexa(data.phone2));

    const emailsMessage: string[] = [];
    if (data.email) emailsMessage.push(data.email);

    const emailsFinancialMessages: string[] = [];
    if (data.emailFinanceiro) {
      emailsFinancialMessages.push(data.emailFinanceiro);
    } else if (data.email) {
      emailsFinancialMessages.push(data.email);
    }

    const payload: Record<string, unknown> = {
      companyId: 3,
      name: data.name,
      tradeName: data.tradeName || null,
      pronunciation: null,
      fieldOfActivity: data.fieldOfActivity || null,
      notes: data.notes || 'Cliente cadastrado via DGenny Deal Plans',
      website: data.website || null,
      hasLoginAccess: false,
      login: null,
      password: null,
      automaticallyIssueNfse: 'notIssue',
      notesNfse: null,
      taxDeductions: {
        iss: true,
      },
      legalPerson: {
        cnpj: formatCnpjForConexa(data.cnpj),
        foundationDate: data.foundationDate || null,
        stateInscription: data.stateInscription || null,
        municipalInscription: null,
      },
      address: {
        zipCode: formatCepForConexa(data.postalCode),
        state: data.state || extractState(data.city),
        city: data.city,
        street: data.address,
        number: data.addressNumber,
        neighborhood: data.province,
        additionalDetails: data.complement || null,
      },
    };

    // Enviar apenas campos com valor para evitar erros de validação na API Conexa
    if (data.phone) {
      payload.cellNumber = formatPhoneForConexa(data.phone);
    }
    if (phones.length > 0) {
      payload.phones = phones;
    }
    if (emailsMessage.length > 0) {
      payload.emailsMessage = emailsMessage;
    }
    if (emailsFinancialMessages.length > 0) {
      payload.emailsFinancialMessages = emailsFinancialMessages;
    }

    return payload as ConexaCustomerPayload;
  },

  /**
   * Prepara o payload para criação de pessoa no Conexa
   * Envia apenas campos com valor para evitar erros de validação
   */
  preparePersonPayload(
    customerId: number,
    responsavel: ResponsavelData,
    endereco?: {
      postalCode: string;
      state: string;
      city: string;
      address: string;
      addressNumber: string;
      province: string;
      complement?: string;
    }
  ): Record<string, unknown> {
    const phones: string[] = [];
    if (responsavel.celular) phones.push(formatPhoneForConexa(responsavel.celular));

    const emails: string[] = [];
    if (responsavel.email) emails.push(responsavel.email);

    // Construir payload apenas com campos que têm valor
    const payload: Record<string, unknown> = {
      customerId,
      name: responsavel.nome,
      isCompanyPartner: responsavel.isSocio || false,
      isGuarantor: false,
      isForeign: false,
    };

    // Adicionar campos opcionais apenas se tiverem valor
    if (responsavel.cpf) {
      payload.cpf = formatCpfForConexa(responsavel.cpf);
    }
    if (responsavel.celular) {
      payload.cellNumber = formatPhoneForConexa(responsavel.celular);
    }
    if (responsavel.cargo) {
      payload.jobTitle = responsavel.cargo;
    }
    if (responsavel.isFinanceiro) {
      payload.notes = 'Responsável Financeiro';
    } else if (responsavel.isAdministrador) {
      payload.notes = 'Sócio Administrador';
    }
    if (emails.length > 0) {
      payload.emails = emails;
    }
    if (phones.length > 0) {
      payload.phones = phones;
    }
    if (endereco) {
      payload.address = {
        zipCode: formatCepForConexa(endereco.postalCode),
        state: endereco.state,
        city: endereco.city,
        street: endereco.address,
        number: endereco.addressNumber,
        neighborhood: endereco.province,
        additionalDetails: endereco.complement || '',
      };
    }

    return payload;
  },

  /**
   * Extrai dados enriquecidos do CNPJ para o payload do Conexa
   */
  extractCnpjDataForPayload(cnpjData: CnpjResponse): {
    name: string;
    tradeName?: string;
    cnpj: string;
    phone: string;
    phone2?: string;
    email: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    complement?: string;
    province: string;
    city: string;
    state: string;
    fieldOfActivity?: string;
    foundationDate?: string;
    stateInscription?: string;
  } {
    const est = cnpjData.estabelecimento;
    const logradouro = est.tipo_logradouro
      ? `${est.tipo_logradouro} ${est.logradouro}`
      : est.logradouro;

    // Buscar inscrição estadual ativa
    const inscricaoEstadual = est.inscricoes_estaduais?.find(ie => ie.ativo)?.inscricao_estadual;

    // Montar telefones
    const phone = est.ddd1 && est.telefone1 ? `${est.ddd1}${est.telefone1}` : '';
    const phone2 = est.ddd2 && est.telefone2 ? `${est.ddd2}${est.telefone2}` : undefined;

    return {
      name: cnpjData.razao_social,
      tradeName: est.nome_fantasia || undefined,
      cnpj: est.cnpj,
      phone,
      phone2,
      email: est.email || '',
      postalCode: est.cep || '',
      address: logradouro || '',
      addressNumber: est.numero || '',
      complement: est.complemento || undefined,
      province: est.bairro || '',
      city: est.cidade?.nome || '',
      state: est.estado?.sigla || '',
      fieldOfActivity: est.atividade_principal?.descricao || undefined,
      foundationDate: est.data_inicio_atividade || undefined,
      stateInscription: inscricaoEstadual || undefined,
    };
  },
};

export default conexaService;
